'use client';

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import NextImage from 'next/image';
import { gsap } from 'gsap';
import { useGuideVerticalProgress } from './useGuideVerticalProgress';
import { ChevronDown } from 'lucide-react';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/components/ui/utils';
import { getImageUrlForVariant } from '@/lib/homepage-mappers';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { HomeBuildingGuideItem } from '../../data/guides';
import type { TransformedGuideInnerData } from '@/types/guide-inner';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

type NullableString = string | null | undefined;

interface CareGuidePageProps {
  locale: string;
  guide: HomeBuildingGuideItem;
  cmsData?: TransformedGuideInnerData | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: React.ReactNode;
}

type CareAccordionItem = {
  id: string;
  title: string;
  description: string;
};

type CareBlock = {
  title: string;
  subtitle: string;
  description: string;
  accordionItems: CareAccordionItem[];
  image: string;
};

const AUTO_ADVANCE_MS = 8000;

const HARDCODED_CARE_BLOCKS: CareBlock[] = [
  {
    title: 'Stop the Dampness',
    subtitle: 'Internal & External Waterproofing',
    description:
      'Water is the single biggest threat to your home’s lifespan. Moisture is a slow poison that rusts the steel inside and peels the paint outside.',
    accordionItems: [
      {
        id: 'roof-barrier',
        title: 'The Roof Barrier',
        description:
          'Most leakage starts at the top. Ensure your terrace is treated with a specialized waterproof coating before the tiles are laid.',
      },
      {
        id: 'wet-area-shield',
        title: 'The Wet-Area Shield',
        description:
          'Bathrooms, balconies, and utility zones need dedicated waterproofing layers and quality sealing around joints.',
      },
    ],
    image: '/images/guid_342.png',
  },
  {
    title: 'Seal the Gaps',
    subtitle: 'Defending the Weak Points',
    description:
      'Even the strongest house has entry points where water and heat can sneak in. Sealing these gaps is like buttoning up a raincoat.',
    accordionItems: [
      {
        id: 'window-door',
        title: 'Window & Door Perimeters',
        description:
          'The tiny spaces between your window frames and the wall are prime spots for rain seepage. Use high-quality sealants to close these gaps permanently.',
      },
      {
        id: 'hairline-crack',
        title: 'Hairline Crack Care',
        description:
          'Address micro-cracks early with crack-fill compounds and protective coatings before they widen during seasonal expansion.',
      },
    ],
    image: '/images/guid_343.png',
  },
  {
    title: 'The Final Shield',
    subtitle: 'Exterior Armour & Terrace Protection',
    description:
      'The outside of your home takes the full force of sun and rain. The final shield is the protective skin that keeps the core of your house safe.',
    accordionItems: [
      {
        id: 'weather-proof',
        title: 'Weather-Proof Coating',
        description:
          'Do not just think of exterior paint as a color choice; it is a protective layer. Use high-performance coatings that resist dust and water.',
      },
      {
        id: 'slab-protection',
        title: 'Heat Protection for the Slab',
        description:
          'Apply reflective terrace coatings to reduce heat absorption and improve indoor comfort while extending roof life.',
      },
    ],
    image: '/images/guid_349.png',
  },
];

function stripHtml(html: NullableString): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&apos;/gi, "'")
    .replace(/&rsquo;/gi, '’')
    .trim();
}

function buildCareBlocksFromCms(
  cmsData: TransformedGuideInnerData | null | undefined,
): CareBlock[] {
  return (cmsData?.accordionBlocks ?? [])
    .filter((b) => !b.otherItemsBlock) // Care has no otherItems blocks
    .map((b) => ({
      title: b.heading ?? '',
      subtitle: b.smallHeading ?? '',
      description: stripHtml(b.description),
      accordionItems: (b.accordionItem ?? []).map((item, idx) => ({
        id: `cms-${b.id}-${item.id ?? idx}`,
        title: item.heading,
        description: stripHtml(item.content),
      })),
      image: getImageUrlForVariant(b.image, 'large') ?? '/images/guid_342.png',
    }));
}

function CareBlockArticle({ block }: { block: CareBlock }) {
  return (
    <article className="overflow-hidden rounded-2xl bg-white lg:h-[430px]">
      <div className="grid h-full grid-cols-1 items-center lg:grid-cols-[1fr_0.92fr]">
        <div className="px-5 py-6 sm:px-6 sm:py-7 lg:px-7 lg:py-7">
          <h3 className="text-2xl font-semibold leading-tight text-[#111827] sm:text-3xl lg:text-4xl">
            {block.title}
          </h3>
          {block.subtitle ? (
            <p className="mt-1 text-lg font-medium leading-tight text-[#1f2937] sm:text-xl lg:text-2xl">
              {block.subtitle}
            </p>
          ) : null}
          <p className="mt-3 text-sm leading-relaxed text-[#4b5563] md:text-base">
            {block.description}
          </p>

          {block.accordionItems.length > 0 ? (
            <div className="mt-5">
              <CareAccordion items={block.accordionItems} />
            </div>
          ) : null}
        </div>

        <div className="relative min-h-[220px] sm:min-h-[260px] lg:h-full">
          <NextImage
            src={block.image}
            alt={block.title}
            fill
            sizes="(max-width: 768px) 100vw, 45vw"
            className="object-cover object-center"
          />
        </div>
      </div>
    </article>
  );
}

function CareAccordion({ items }: { items: CareAccordionItem[] }) {
  const [activeId, setActiveId] = React.useState(items[0]?.id ?? '');
  const progressRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const progressTween = React.useRef<gsap.core.Tween | null>(null);

  const goToNext = useCallback(() => {
    const currentIndex = items.findIndex((x) => x.id === activeId);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0;
    const nextId = items[nextIndex]?.id;
    if (nextId) setActiveId(nextId);
  }, [activeId, items]);

  useEffect(() => {
    for (const item of items) {
      const isActive = item.id === activeId;
      const progressEl = progressRefs.current[item.id];
      if (progressEl && !isActive) {
        gsap.killTweensOf(progressEl);
        gsap.set(progressEl, { width: '0%' });
      }
    }

    const activeProgressEl = progressRefs.current[activeId];
    if (activeProgressEl) {
      progressTween.current?.kill();
      gsap.set(activeProgressEl, { width: '0%' });
      progressTween.current = gsap.to(activeProgressEl, {
        width: '100%',
        duration: AUTO_ADVANCE_MS / 1000,
        ease: 'none',
        onComplete: goToNext,
      });
    }

    return () => {
      progressTween.current?.kill();
    };
  }, [activeId, goToNext, items]);

  return (
    <div className="divide-y divide-[#D8D8D8] border-y border-[#D8D8D8]">
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <div key={item.id} className="relative">
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-[2px] overflow-hidden bg-[#ECECEC]">
              <span
                ref={(el) => {
                  progressRefs.current[item.id] = el;
                }}
                className="absolute inset-y-0 left-0 w-0 bg-[#E31E24]"
              />
            </div>

            <button
              type="button"
              onClick={() => setActiveId(item.id)}
              className={cn(
                'flex w-full items-center justify-between gap-4 px-0 py-4 text-left transition-colors',
                isActive ? 'text-[#151515]' : 'text-[#242424]',
              )}
            >
              <span className="text-base font-medium leading-tight sm:text-[18px]">
                {item.title}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-[#2A2A2A] transition-transform duration-300',
                  isActive && 'rotate-180',
                )}
              />
            </button>

            <div className={cn(!isActive && 'hidden')}>
              <p className="pb-4 pr-6 text-[12px] leading-[1.55] text-[#515151]">
                {item.description}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CareChecklistSection({
  checklist,
}: {
  checklist: NonNullable<TransformedGuideInnerData['checklist']>;
}) {
  return (
    <section className="bg-[#efefef] py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_1fr] lg:gap-14">
          <div className="max-w-[560px]">
            <h3 className="text-3xl font-semibold leading-[1.08] text-[#111827] sm:text-4xl lg:text-4xl">
              {checklist.heading ?? 'Home Construction Care Checklist'}
            </h3>
            {checklist.subHeading ? (
              <div
                className="mt-4 max-w-md text-sm leading-relaxed text-[#4b5563] md:text-base [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: checklist.subHeading }}
              />
            ) : null}
          </div>

          <div className="rounded-xl bg-white px-5 py-5 sm:px-6 sm:py-6 lg:px-7 lg:py-7">
            <ul className="space-y-4">
              {checklist.data.map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  <NextImage
                    src="/images/checkgrn.svg"
                    alt=""
                    aria-hidden
                    width={16}
                    height={16}
                    className="mt-1 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-semibold leading-tight text-[#111827] md:text-base">
                      {item.heading}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-[#6b7280] md:text-sm">
                      {stripHtml(item.description)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CareToolkitSection({
  toolkit,
  toolkitButtons,
  locale,
  resolveButtonHref,
}: {
  toolkit: NonNullable<TransformedGuideInnerData['toolkit']>;
  toolkitButtons: NonNullable<TransformedGuideInnerData['toolkit']>['buttonWhite'];
  locale: string;
  resolveButtonHref: (buttonUrl: NullableString, fallback: string) => string;
}) {
  return (
    <section className="bg-[#efefef] pb-12 md:pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="relative overflow-hidden rounded-2xl bg-[#3a0004] px-5 py-6 sm:px-6 lg:px-10 max-sm:!pb-0">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_0.95fr]">
            <div className="relative z-10 max-w-[520px] text-white lg:py-14">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {toolkit.heading ?? 'Your Precision Toolkit'}
              </p>
              {toolkit.subHeading ? (
                <div
                  className="mt-3 max-w-sm text-xl font-semibold leading-tight sm:text-2xl [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: toolkit.subHeading }}
                />
              ) : null}

              {toolkitButtons.length > 0 ? (
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {toolkitButtons.map((btn, idx) => {
                    const isOutlined = btn.buttonType === 'Transparent';
                    const href = resolveButtonHref(btn.buttonUrl, `/${locale}/tools`);
                    return (
                      <SwapTextButton
                        key={btn.id ?? idx}
                        text={btn.buttonText}
                        hoverText={btn.buttonText}
                        href={href}
                        {...getCmsButtonLinkProps(btn)}
                        tone={isOutlined ? undefined : 'light'}
                        variant={isOutlined ? 'outline' : undefined}
                        showArrow={false}
                        className={cn(
                          'min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto',
                          isOutlined &&
                            '!border-white !bg-transparent !text-white hover:!bg-white hover:!text-[#111827]',
                        )}
                        aria-label={btn.ariaLabel ?? btn.buttonText}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="relative min-h-[220px] sm:min-h-[260px] lg:min-h-[320px]">
              <NextImage
                src={getImageUrlForVariant(toolkit.image, 'large') ?? '/images/guid_350.png'}
                alt={toolkit.heading ?? 'Toolkit'}
                fill
                sizes="(max-width: 768px) 100vw, 45vw"
                className="object-cover object-right"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CareGuideHero({
  heroImage,
  heroMedia,
  heroAlt,
  heroWidth,
  heroHeight,
  heroTitle,
  heroTitleRichText,
  heroSubtitle,
  heroDescription,
}: {
  heroImage: string;
  heroMedia: TransformedGuideInnerData['hero']['bannerMedia'] | null;
  heroAlt: string;
  heroWidth: number;
  heroHeight: number;
  heroTitle: string;
  heroTitleRichText: string | null;
  heroSubtitle: string;
  heroDescription: string;
}) {
  return (
    <section className="overflow-hidden bg-black font-figtree">
      <div className="lg:hidden">
        <CmsImage
          src={heroImage}
          media={heroMedia}
          alt={heroAlt}
          width={heroWidth}
          height={heroHeight}
          priority
          loading="eager"
          sizes="100vw"
          className="h-auto w-full"
          posterSrc={MEDIA_PLACEHOLDER}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />

        <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10 md:py-12">
          <div className="grid gap-6">
            <div className="max-w-[640px]">
              <CmsRichTextHeading
                className="hero-title text-white"
                html={heroTitleRichText}
                fallback={heroTitle}
              />
            </div>

            <div className="max-w-[420px]">
              <h2 className="text-xl font-medium leading-tight text-white sm:text-2xl md:text-[1.7rem]">
                {heroSubtitle}
              </h2>
              <p className="hero-subtitle mt-4 text-white/85">{heroDescription}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden h-[100svh] overflow-hidden bg-black lg:block">
        <CmsImage
          src={heroImage}
          media={heroMedia}
          alt={heroAlt}
          fill
          priority
          loading="eager"
          sizes="100vw"
          className="object-cover object-center"
          posterSrc={MEDIA_PLACEHOLDER}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/72 via-black/35 to-black/72" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/15" />

        <div className="relative z-10 h-full">
          <div className="container mx-auto h-full">
            <div className="grid h-full w-full grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(240px,260px)] xl:grid-cols-[minmax(0,1fr)_minmax(300px,450px)]">
              <div className="max-w-[430px] self-start pt-[148px] xl:max-w-[640px] xl:pt-[168px]">
                <CmsRichTextHeading
                  className="hero-title text-balance text-white"
                  html={heroTitleRichText}
                  fallback={heroTitle}
                />
              </div>

              <div className="max-w-[370px] self-end pb-[72px] lg:justify-self-end xl:max-w-[400px]">
                <h2 className="text-[18px] font-semibold leading-[1.35] tracking-[-0.015em] text-white xl:text-[24px]">
                  {heroSubtitle}
                </h2>
                <p className="mt-2 text-[14px] leading-[1.55] tracking-[-0.01em] text-white/85 xl:mt-3 xl:text-[16px]">
                  {heroDescription}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Resolve the hero block props (CMS values with hardcoded fallbacks). */
function resolveCareHero(
  cmsData: TransformedGuideInnerData | null | undefined,
  guide: HomeBuildingGuideItem,
) {
  const heroMedia = cmsData?.hero.bannerMedia ?? null;
  return {
    heroTitle: cmsData?.hero.heading || 'Mastering Home Construction Care',
    heroTitleRichText: cmsData?.hero.titleRichText ?? null,
    heroSubtitle:
      stripHtml(cmsData?.hero.subheadingHtml) ||
      'Your home is a lifetime investment. Here is how to protect it.',
    heroDescription:
      stripHtml(cmsData?.hero.descriptionHtml) ||
      'Construction care is the final, most critical phase where your transition from building a structure to protecting a legacy. It is the bridge between a newly finished house and a home that stays pristine for decades.',
    heroMedia,
    heroImage: getStrapiOriginalMediaUrl(heroMedia) ?? '/images/guild_351.jpg',
    heroAlt: heroMedia?.alternativeText || guide.title,
    heroWidth: heroMedia?.width || 1440,
    heroHeight: heroMedia?.height || 810,
  };
}

export default function CareGuidePage({ locale, guide, cmsData, faqSlot }: CareGuidePageProps) {
  const careFlowSectionRef = useRef<HTMLDivElement | null>(null);
  const careFlowContentRef = useRef<HTMLDivElement | null>(null);
  const progressColumnRef = useRef<HTMLDivElement | null>(null);
  const progressPinRef = useRef<HTMLDivElement | null>(null);
  const progressRailRef = useRef<HTMLDivElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const progressDotRef = useRef<HTMLSpanElement | null>(null);

  // Hero copy — every field falls back to the existing hardcoded design when
  // CMS is missing, so the page never renders blank.
  const {
    heroTitle,
    heroTitleRichText,
    heroSubtitle,
    heroDescription,
    heroMedia,
    heroImage,
    heroAlt,
    heroWidth,
    heroHeight,
  } = resolveCareHero(cmsData, guide);

  // Optional intro paragraph above the accordion blocks.
  const introHtml = cmsData?.introText?.description ?? null;

  // CMS accordion blocks (3 of them) → CareBlock array. Falls back to the
  // baked-in copy when CMS hasn't loaded.
  const cmsBlocks: CareBlock[] = buildCareBlocksFromCms(cmsData);
  const careBlocks = cmsBlocks.length > 0 ? cmsBlocks : HARDCODED_CARE_BLOCKS;

  const checklist = cmsData?.checklist;
  const toolkit = cmsData?.toolkit;
  const toolkitButtons = [...(toolkit?.buttonWhite ?? []), ...(toolkit?.buttonDark ?? [])];

  const resolveButtonHref = (buttonUrl: NullableString, fallback: string) => {
    if (!buttonUrl) return fallback;
    return buttonUrl.startsWith('http') ? buttonUrl : `/${locale}${buttonUrl}`;
  };

  useGuideVerticalProgress(
    {
      sectionRef: careFlowSectionRef,
      contentRef: careFlowContentRef,
      progressColumnRef,
      progressPinRef,
      progressRailRef,
      progressFillRef,
      progressDotRef,
    },
    careBlocks.length,
  );

  return (
    <>
      <CareGuideHero
        heroImage={heroImage}
        heroMedia={heroMedia}
        heroAlt={heroAlt}
        heroWidth={heroWidth}
        heroHeight={heroHeight}
        heroTitle={heroTitle}
        heroTitleRichText={heroTitleRichText}
        heroSubtitle={heroSubtitle}
        heroDescription={heroDescription}
      />

      <section
        className={`${cmsData && cmsData.accordionBlocks.length === 0 ? 'hidden' : ''} bg-black py-10 md:py-14`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          {introHtml ? (
            <div
              className="mb-6 max-w-4xl text-base font-medium leading-normal text-white/90 sm:text-lg md:mb-8 lg:text-2xl [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-white"
              dangerouslySetInnerHTML={{ __html: introHtml }}
            />
          ) : (
            <p className="mb-6 max-w-4xl text-base font-medium leading-normal text-white/90 sm:text-lg md:mb-8 lg:text-2xl">
              Here&apos;s your guide to shielding your home against rain, heat, and time to ensure
              its strength lasts for generations.
            </p>
          )}

          <div
            ref={careFlowSectionRef}
            className="relative grid grid-cols-1 gap-6 lg:grid-cols-[26px_1fr] lg:gap-8"
          >
            <div ref={progressColumnRef} className="relative hidden w-[26px] shrink-0 lg:block">
              <div ref={progressPinRef} className="relative h-full w-full">
                <div
                  ref={progressRailRef}
                  className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-white/25"
                  aria-hidden
                />
                <div
                  ref={progressFillRef}
                  className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-[#ed1c25]"
                />
                <span
                  ref={progressDotRef}
                  className="absolute left-1/2 top-0 z-[1] size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ed1c25]"
                />
              </div>
            </div>

            <div ref={careFlowContentRef} className="space-y-5 sm:space-y-6 lg:space-y-8">
              {careBlocks.map((block) => (
                <CareBlockArticle key={block.title} block={block} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {checklist ? <CareChecklistSection checklist={checklist} /> : null}

      {toolkit ? (
        <CareToolkitSection
          toolkit={toolkit}
          toolkitButtons={toolkitButtons}
          locale={locale}
          resolveButtonHref={resolveButtonHref}
        />
      ) : null}

      {/* FAQ + Enquiry Form — streamed via Suspense from page.tsx so the
          lead-forms fetch doesn't block above-the-fold paint. */}
      {faqSlot}
    </>
  );
}
